/**
 * @file hooks/useFieldWriteAccess.ts
 * @description 現場作業「可操作 / 唯讀」判斷（序61）。
 *
 * 需求序61：管理組（manager）沒有現場作業的操作權限，只能查看、不能操作。
 * 現場作業的寫入能力（打卡 / 採樣 / 開立 NC / 提交稽核記錄等）在後端能力矩陣
 * （`packages/shared/src/auth/can.ts`）本就僅授予 auditor；本 hook 讓前端據同一口徑
 * 隱藏 / 置灰對應按鈕，避免 manager 點擊後才吃 403。
 *
 * 用法：
 * ```ts
 * const { canCheckIn, canSample, canFinding } = useFieldWriteAccess();
 * // 或針對任一能力：
 * const canDo = useCan('sample.create');
 * ```
 *
 * 多角色模型：一律用 `userCan(user, capability)`（動態 RBAC 優先，回落能力並集），不硬編角色。
 * user 尚未由持久化恢復（hard refresh 首帧）時回落 null → 全 false，避免誤判。
 */

import { useMemo } from 'react';
import { userCan, type Capability } from '@zhcb/shared';
import { useAuthStore } from '@/lib/auth-context';

/**
 * 取得當前使用者對象（含 capabilities 動態能力集，供 userCan 使用）。
 * 回落處理：舊 persist 資料可能無 roles，useMemo 穩定引用避免不必要重渲染。
 */
function useAuthUser() {
  return useAuthStore((s) => s.user);
}

/**
 * 判斷當前使用者是否具備指定能力（動態 RBAC 優先，回落 roles 矩陣）。
 * @param capability 能力鍵（如 `sample.create`）
 */
export function useCan(capability: Capability): boolean {
  const user = useAuthUser();
  // userCan：有 capabilities 時以能力集為準；無則回落 roles → DEFAULT_ROLE_CAPABILITIES
  return useMemo(() => (user ? userCan(user, capability) : false), [user, capability]);
}

/**
 * 現場作業各項寫入能力的集合判斷（序61）。
 *
 * 每個布林對應一項後端能力；manager 對這些能力皆為 false，故現場作業所有操作按鈕
 * 都會被隱藏/置灰，只保留內容瀏覽（唯讀）。
 * 多角色模型：一律用 `userCan(user, capability)`（動態 RBAC 優先，回落能力並集），不硬編角色。
 * roles 尚未由持久化恢復（hard refresh 首帧）時 user 為 null，回傳全 false，避免誤判。
 */
export function useFieldWriteAccess() {
  const user = useAuthUser();
  return useMemo(
    () => ({
      /** 現場到達/離開打卡（check-in/check-out） */
      canCheckIn: user ? userCan(user, 'audit-task.checkin') : false,
      /** 採樣登記 / 送檢 */
      canSample: user ? userCan(user, 'sample.create') : false,
      /** 開立 NC / 發現事項 */
      canFinding: user ? userCan(user, 'audit-finding.create') : false,
      /** 提交稽核記錄 */
      canRecord: user ? userCan(user, 'audit-record.create') : false,
      /**
       * 現場作業整體是否可操作（任一現場寫入能力）。
       * 供「現場拍照」等未細分能力的通用寫入區塊使用（純 auditor 才可，manager 為 false）。
       */
      canFieldWrite: user ? userCan(user, 'audit-task.checkin') : false,
    }),
    [user],
  );
}
